import { useContext, useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import CustomSpinner from "../components/CustomSpinner";
import UserContext from "../context/UserContext";
import productService from "../services/productService";
import userService from "../services/userService";
import { PIECEWORK_TYPES } from "../constants/business";

const emptyProduction = { secondOperatorId: "", quantity: 1 };

const parseRecipe = (recipe) => {
  try { return Array.isArray(recipe) ? recipe : JSON.parse(recipe || "[]"); }
  catch { return []; }
};

export default function Production() {
  const { user } = useContext(UserContext);
  const [products, setProducts] = useState([]);
  const [productionWorkers, setProductionWorkers] = useState([]);
  const [production, setProduction] = useState(emptyProduction);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [result, setResult] = useState("Producido");
  const [confirmation, setConfirmation] = useState(false);
  const [pieces, setPieces] = useState([]);
  const [printPieces, setPrintPieces] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadProducts = () => {
    setLoading(true);
    productService.getAll()
      .then(({ data }) => setProducts(data))
      .catch((error) => alert(error.response?.data?.message || "No se pudieron cargar los productos."))
      .finally(() => setLoading(false));
  };

  const loadProductionWorkers = () => userService.getProductionWorkers()
    .then(({ data }) => setProductionWorkers(data))
    .catch((error) => alert(error.response?.data?.message || "No se pudieron cargar los operarios."));

  useEffect(() => {
    if (user) {
      loadProducts();
      loadProductionWorkers();
    }
  }, [user]);

  useEffect(() => {
    if (!printPieces.length) return undefined;
    const timer = window.setTimeout(() => window.print(), 0);
    const finishPrint = () => setPrintPieces([]);
    window.addEventListener("afterprint", finishPrint, { once: true });
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("afterprint", finishPrint);
    };
  }, [printPieces]);

  const update = (field) => (event) => setProduction({ ...production, [field]: event.target.value });
  const selectedProduct = products.find(({ id }) => String(id) === selectedProductId);
  const recipe = selectedProduct ? parseRecipe(selectedProduct.recipe) : [];
  const getProductName = (productId) => products.find(({ id }) => String(id) === String(productId))?.name || "Insumo no encontrado";

  const openConfirmation = (event) => {
    event.preventDefault();
    const quantity = Number(production.quantity);
    if (!selectedProduct || !Number.isInteger(quantity) || quantity < 1) {
      alert("Captura un producto y una cantidad válida.");
      return;
    }
    setConfirmation(true);
  };

  const produce = async () => {
    const quantity = Number(production.quantity);
    const target = result === "Merma" ? products.find(({ name }) => name === `Merma ${selectedProduct.name}`) : selectedProduct;
    if (!target) {
      alert(`No existe el producto ${`Merma ${selectedProduct.name}`} en el catálogo.`);
      return;
    }

    setConfirmation(false);
    setLoading(true);
    try {
      const { data } = await productService.updateStock(target.id, quantity, {
        reason: result,
        production: true,
        operatorName: user.name,
        secondOperatorId: production.secondOperatorId || null,
      });
      setPieces(data.pieces || []);
      setProduction(emptyProduction);
      setSelectedProductId("");
      setResult("Producido");
      loadProducts();
    } catch (error) {
      alert(error.response?.data?.message || "No se pudo registrar la producción.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-shell banner-1">
      {loading && <CustomSpinner />}
      <div className="app-panel max-w-6xl text-center">
        <div className="mb-6 text-left"><h1 className="text-2xl font-semibold">Producción</h1></div>
        <form className="mb-6 grid gap-3 text-left md:grid-cols-3" onSubmit={openConfirmation}>
          <label className="block text-sm font-medium">Segundo operario<select className="form-control mt-2" value={production.secondOperatorId} onChange={update("secondOperatorId")}><option value="">Opcional</option>{productionWorkers.filter(({ id }) => String(id) !== String(user?.id)).map(({ id, name }) => <option key={id} value={id}>{name}</option>)}</select></label>
          <label className="block text-sm font-medium">Cantidad<input className="form-control mt-2" type="number" min="1" step="1" value={production.quantity} onChange={update("quantity")} /></label>
          <label className="block text-sm font-medium">Producto<select className="form-control mt-2" value={selectedProductId} onChange={(event) => setSelectedProductId(event.target.value)}>
            <option value="">Seleccionar producto</option>
            {products.filter(({ catalogType, name }) => catalogType === "Producto terminado" && !name.startsWith("Merma ")).map(({ id, name, stock, unit, pieceworkType }) => <option key={id} value={id}>{name} ({PIECEWORK_TYPES.find(({ value }) => value === pieceworkType)?.label || "Sin tipo de destajo"}, {stock} {unit})</option>)}
          </select></label>
          <label className="block text-sm font-medium">Resultado<select className="form-control mt-2" value={result} onChange={(event) => setResult(event.target.value)}>
            <option value="Producido">Producido</option><option value="Merma">Merma</option>
          </select></label>
          <div className="flex items-end"><button className="btn btn-info w-full" type="submit">Registrar</button></div>
        </form>

        {selectedProduct && <section className="mb-6 rounded-2xl border border-black/10 p-4 text-left dark:border-white/10">
          <h2 className="text-lg font-semibold">Receta de {selectedProduct.name}</h2>
          {recipe.length > 0 ? <ul className="mt-3 divide-y divide-black/10 text-sm dark:divide-white/10">
            {recipe.map((item, index) => <li className="flex justify-between py-2" key={`${item.productId}-${index}`}><span>{getProductName(item.productId)}</span><strong>{item.quantity}</strong></li>)}
          </ul> : <p className="mt-2 text-sm text-slate-500">Este producto no tiene receta registrada.</p>}
        </section>}

        {confirmation && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-2xl border border-black/10 bg-slate-50 p-5 text-left shadow-2xl dark:border-white/10 dark:bg-slate-900">
            <h2 className="text-lg font-semibold">Confirmar producción</h2>
            <dl className="mt-4 space-y-2 text-sm"><div className="flex justify-between gap-4"><dt>Producto</dt><dd className="font-semibold">{selectedProduct.name}</dd></div><div className="flex justify-between gap-4"><dt>Resultado</dt><dd className="font-semibold">{result}</dd></div><div className="flex justify-between gap-4"><dt>Folio</dt><dd className="font-semibold">Se genera automáticamente</dd></div><div className="flex justify-between gap-4"><dt>Cantidad</dt><dd className="font-semibold">{production.quantity}</dd></div><div className="flex justify-between gap-4"><dt>Segundo operario</dt><dd className="font-semibold">{productionWorkers.find(({ id }) => String(id) === production.secondOperatorId)?.name || "No indicado"}</dd></div></dl>
            <div className="mt-6 flex justify-end gap-2"><button className="btn border-slate-300 dark:border-slate-700" onClick={() => setConfirmation(false)}>Cancelar</button><button className="btn btn-success" onClick={produce}>Aceptar</button></div>
          </div>
        </div>}

        {pieces.length > 0 && <section className="mt-8 text-left">
          <div className="mb-4 flex items-center justify-between"><h2 className="text-xl font-semibold">Etiquetas generadas</h2><button className="btn btn-info" onClick={() => setPrintPieces(pieces)}>Imprimir</button></div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {pieces.map((piece) => <article key={piece.code} className="rounded-xl border border-black/10 bg-white p-4 text-center dark:border-white/10 dark:bg-slate-900"><QRCodeSVG value={piece.code} size={150} className="mx-auto" /><p className="mt-2 font-mono text-sm">{piece.folio || piece.code}</p><p className="text-xs text-slate-500">{piece.productName}</p><button className="btn btn-info mt-3" onClick={() => setPrintPieces([piece])}>Imprimir</button></article>)}
          </div>
        </section>}
        {printPieces.length > 0 && <div className="print-labels" aria-hidden="true">{printPieces.map((piece) => <div className="print-label" key={piece.code}><QRCodeSVG value={piece.code} size={180} /><strong>{piece.folio || piece.code}</strong></div>)}</div>}
      </div>
    </div>
  );
}
